import { useState, useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import { StrokeSyncService } from '../services/StrokeSyncService';

export interface NetworkStatus {
    isOnline: boolean;
    wasOffline: boolean;
    retrySync: () => void;
}

/**
 * useNetworkStatus — tracks browser/PWA connectivity.
 * Listens to online/offline window events on web, and triggers
 * auto-sync of pending submissions and task completions when
 * connectivity returns.
 */
const isInitialOnline = Platform.OS === 'web' && typeof navigator !== 'undefined'
    ? navigator.onLine !== false
    : true;

export function useNetworkStatus(): NetworkStatus {
    const [isOnline, setIsOnline] = useState<boolean>(isInitialOnline);
    const [wasOffline, setWasOffline] = useState<boolean>(false);

    const retrySync = useCallback(() => {
        StrokeSyncService.flushOfflineSubmissions().catch(err => {
            console.warn('[useNetworkStatus] Error flushing offline data:', err);
        });
    }, []);

    useEffect(() => {
        if (
            Platform.OS !== 'web' ||
            typeof window === 'undefined' ||
            typeof window.addEventListener !== 'function'
        ) {
            return;
        }

        const handleOnline = () => {
            console.log('[useNetworkStatus] Connection restored — device is online');
            setIsOnline(true);
            setWasOffline(true);
            retrySync();
        };

        const handleOffline = () => {
            console.log('[useNetworkStatus] Connection lost — device is offline');
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
                window.removeEventListener('online', handleOnline);
                window.removeEventListener('offline', handleOffline);
            }
        };
    }, [retrySync]);

    return { isOnline, wasOffline, retrySync };
}
